"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.DateUtil = exports.mergeDateAndTime = exports.daysBetween = exports.isSameDay = exports.formatWireDateTimeNullable = exports.formatWireDateTime = exports.formatWireDateNullable = exports.parseWireDate = exports.formatWireDate = exports.formatDateTimeNoSecondsNullable = exports.formatDateTimeNoSeconds = exports.formatDateTimeNullable = exports.formatDateTime = exports.formatTimeNullable = exports.formatTime = exports.getWeekdayAsString = exports.isValidDate = exports.formatDateNullable = exports.formatDate = exports.parseDate = exports.LuxonDuration = exports.LuxonInterval = exports.LuxonDateTime = exports.getMoment = void 0;
const tslib_1 = require("tslib");
const moment_1 = tslib_1.__importDefault(require("moment"));
const luxon_1 = require("luxon");
const DATE_FORMAT = "DD-MM-YYYY";
// Luxon format https://moment.github.io/luxon/#/formatting?id=table-of-tokens
const DANISH_DATE_FORMAT = "dd-MM-yyyy";
const DANISH_DATE_TIME_FORMAT = "dd-MM-yyyy HH:mm:ss";
const DANISH_DATE_TIME_HHMM = "dd-MM-yyyy HH:mm";
const DATE_FORMAT_ISO_8601 = "YYYY-MM-DD";
luxon_1.Settings.defaultLocale = "da";
luxon_1.Settings.throwOnInvalid = true;
/** TODO: Usages of this should be replaced by calls to conversion functions like the ones below
 - * Get the date as a moment object
 - * @param date Date or string
 + * @returns Date or undefined, if value does not express a valid date
 */
function getMoment(date) {
    return date ? (0, moment_1.default)(date) : (0, moment_1.default)();
}
exports.getMoment = getMoment;
exports.LuxonDateTime = luxon_1.DateTime;
exports.LuxonInterval = luxon_1.Interval;
exports.LuxonDuration = luxon_1.Duration;
function getInternalDateTime(date) {
    if (date instanceof Date) {
        return luxon_1.DateTime.fromJSDate(date);
    }
    else {
        return luxon_1.DateTime.fromISO(date);
    }
}
// date conversion
// --------------------------------------------------------------------------------------------------------------------
/**
 * Parse date string
 * @param value string to parse, format DD-MM-YYYY
 * @returns Date or undefined, if value does not express a valid date
 */
function parseDate(value) {
    if (!value) {
        return undefined;
    }
    const momentDate = (0, moment_1.default)(value, DATE_FORMAT, true);
    return momentDate.isValid() ? momentDate.toDate() : undefined;
}
exports.parseDate = parseDate;
/**
 * Format date for presentation
 * @param date Date or string in format YYYY-MM-DD
 * @returns Date as string in format DD-MM-YYYY, or blank if date is undefined
 */
function formatDate(date) {
    if (date === undefined) {
        return "";
    }
    return getInternalDateTime(date).toFormat(DANISH_DATE_FORMAT);
}
exports.formatDate = formatDate;
function formatDateNullable(date) {
    if (!date) {
        return;
    }
    return formatDate(date);
}
exports.formatDateNullable = formatDateNullable;
function isValidDate(value) {
    const momentDate = (0, moment_1.default)(value, DATE_FORMAT, true);
    return momentDate.isValid();
}
exports.isValidDate = isValidDate;
function getWeekdayAsString(date) {
    if (!date) {
        return "";
    }
    return getInternalDateTime(date).weekdayLong;
}
exports.getWeekdayAsString = getWeekdayAsString;
// time conversion
// --------------------------------------------------------------------------------------------------------------------
/**
 * Format time only for presentation
 * @param date Date or string
 */
function formatTime(date) {
    return getInternalDateTime(date).toISOTime({
        suppressMilliseconds: true,
        includeOffset: false
    });
}
exports.formatTime = formatTime;
function formatTimeNullable(date) {
    if (!date) {
        return;
    }
    return formatTime(date);
}
exports.formatTimeNullable = formatTimeNullable;
// date/time conversion
// --------------------------------------------------------------------------------------------------------------------
/**
 * Format date and time for presentation
 * @param date Date or string
 */
function formatDateTime(date) {
    return getInternalDateTime(date).toFormat(DANISH_DATE_TIME_FORMAT);
}
exports.formatDateTime = formatDateTime;
function formatDateTimeNullable(date) {
    if (!date) {
        return;
    }
    return formatDateTime(date);
}
exports.formatDateTimeNullable = formatDateTimeNullable;
/**
 * Format date and time (excluding seconds) for presentation
 * @param date Date or string
 */
function formatDateTimeNoSeconds(date) {
    return getInternalDateTime(date).toFormat(DANISH_DATE_TIME_HHMM);
}
exports.formatDateTimeNoSeconds = formatDateTimeNoSeconds;
function formatDateTimeNoSecondsNullable(date) {
    if (!date) {
        return;
    }
    return formatDateTimeNoSeconds(date);
}
exports.formatDateTimeNoSecondsNullable = formatDateTimeNoSecondsNullable;
/**
 * Format date only (no time) for wire representation
 * @param date Date or string
 */
function formatWireDate(date) {
    return getInternalDateTime(date).toISODate();
}
exports.formatWireDate = formatWireDate;
/**
 * Parse date string
 * @param value string to parse, format YYYY-MM-DD
 * @returns Date or undefined, if value does not express a valid date
 */
function parseWireDate(value) {
    if (!value) {
        return undefined;
    }
    const momentDate = (0, moment_1.default)(value, DATE_FORMAT_ISO_8601, true);
    return momentDate.isValid() ? momentDate.toDate() : undefined;
}
exports.parseWireDate = parseWireDate;
function formatWireDateNullable(date) {
    if (!date) {
        return;
    }
    return formatWireDate(date);
}
exports.formatWireDateNullable = formatWireDateNullable;
/**
 * Format date and time for wire representation
 * @param date Date or string
 */
function formatWireDateTime(date) {
    return getInternalDateTime(date)
        .toUTC()
        .toISO({
        suppressMilliseconds: true,
        includeOffset: true
    });
}
exports.formatWireDateTime = formatWireDateTime;
function formatWireDateTimeNullable(date) {
    if (!date) {
        return;
    }
    return formatWireDateTime(date);
}
exports.formatWireDateTimeNullable = formatWireDateTimeNullable;
function isSameDay(d1, d2) {
    if (!d1 || !d2) {
        return false;
    }
    return getInternalDateTime(d1).hasSame(getInternalDateTime(d2), "day");
}
exports.isSameDay = isSameDay;
/**
 * Calculates the number of days between {@param startDate} and {@param endDate} both days INCLUSIVE.
 * E.g.
 * daysBetween("01-01-2022", "03-01-2022") === 3:
 *      "01-01-2022" = 1st day (+1)
 *      "01-02-2022" = 2nd day (+1)
 *      "01-03-2022" = 3rd day (+1)
 *      = 3 days total.
 *
 * @param {Date | string | Null} startDate
 * @param {Date | string | Null} endDate
 * @returns {number} days between {@param startDate} and {@param endDate} or NaN if either of {@param startDate} or {@param endDate} is falsy.
 */
function daysBetween(startDate, endDate) {
    if (!startDate || !endDate) {
        return NaN;
    }
    const startDateTime = getInternalDateTime(startDate).startOf("day");
    const endDateTime = getInternalDateTime(endDate).startOf("day");
    // Reverse behaviour to match moment
    return endDateTime.diff(startDateTime, "day").days + 1; // We do +1 to include the endDate.;
}
exports.daysBetween = daysBetween;
function mergeDateAndTime(args) {
    const { date, hours = 0, minutes = 0, seconds = 0 } = args;
    if (!date) {
        return;
    }
    return getInternalDateTime(date)
        .startOf("day")
        .plus({
        hour: hours || 0,
        minute: minutes || 0,
        second: seconds || 0
    }).toJSDate();
}
exports.mergeDateAndTime = mergeDateAndTime;
class DateUtil {
    static getLocaleWeekDay(date) {
        return getInternalDateTime(date).weekdayLong;
    }
    // time conversion
    // --------------------------------------------------------------------------------------------------------------------
    static formatTime(date) {
        return getInternalDateTime(date).toISOTime({
            suppressMilliseconds: true,
            includeOffset: false
        });
    }
    static formatDanishDateTime(date) {
        return getInternalDateTime(date).toFormat(DANISH_DATE_TIME_FORMAT);
    }
    static formatDanishDateTimeNoSeconds(date) {
        return getInternalDateTime(date).toFormat(DANISH_DATE_TIME_HHMM);
    }
    static formatISODate(date) {
        return getInternalDateTime(date).toISODate();
    }
    static formatISODateTime(date) {
        return getInternalDateTime(date)
            .toUTC()
            .toISO({
            suppressMilliseconds: true,
            includeOffset: true
        });
    }
    static isSameDay(d1, d2) {
        return getInternalDateTime(d1).hasSame(getInternalDateTime(d2), "day");
    }
    static formatDanishDate(date) {
        return getInternalDateTime(date).toFormat(DANISH_DATE_FORMAT);
    }
    static daysBetween(startDate, endDate) {
        const startDateTime = getInternalDateTime(startDate).startOf("day");
        const endDateTime = getInternalDateTime(endDate).startOf("day");
        // Reverse behaviour to match moment
        return endDateTime.diff(startDateTime, "day").days + 1; // We do +1 to include the endDate.;
    }
    static isSame(intervalAndUnit) {
        const { a, b, dateTimeUnit } = DateUtil.convertToInternalIntervalAndUnit(intervalAndUnit);
        return a.hasSame(b, dateTimeUnit);
    }
    static isABeforeB(intervalAndUnit) {
        const duration = DateUtil.getDurationInternal(intervalAndUnit);
        return (Math.trunc(duration) || 0) > 0; // avoid -0
    }
    static isAAfterB(intervalAndUnit) {
        const duration = DateUtil.getDurationInternal(intervalAndUnit);
        return (Math.trunc(duration) || 0) < 0; // avoid -0
    }
    static getDurationAsInteger(intervalAndUnit) {
        const duration = DateUtil.getDurationInternal(intervalAndUnit);
        return Math.trunc(duration) || 0; // avoid -0
    }
    static getDurationInternal(intervalAndUnit) {
        const { a, b, dateTimeUnit } = DateUtil.convertToInternalIntervalAndUnit(intervalAndUnit);
        const diff = b.diff(a);
        return diff.as(dateTimeUnit) || 0;
    }
    static convertToInternalIntervalAndUnit({ a, b, dateTimeUnit = "millisecond" }) {
        let dateTimeA;
        let dateTimeB;
        if (a instanceof Date) {
            dateTimeA = luxon_1.DateTime.fromJSDate(a);
        }
        else if (typeof a === "string") {
            dateTimeA = luxon_1.DateTime.fromISO(a);
        }
        else {
            dateTimeA = a;
        }
        if (b instanceof Date) {
            dateTimeB = luxon_1.DateTime.fromJSDate(b);
        }
        else if (typeof b === "string") {
            dateTimeB = luxon_1.DateTime.fromISO(b);
        }
        else {
            dateTimeB = b;
        }
        return { a: dateTimeA, b: dateTimeB, dateTimeUnit: dateTimeUnit };
    }
}
exports.DateUtil = DateUtil;
//# sourceMappingURL=DateUtil.js.map